import { CompanionPanel } from '@/components/companion/feed/CompanionPanel';
import { useCompanionFeedQuery } from '@/hooks/queries/use-companion-feed-query';
import { companionPanelScope, useCompanionPanelStore } from '@/stores/companion-panel-store';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Where the panel lives in the shell: outside the scrolling content, next to
 * the drawers, so it can float over the page without being clipped by it.
 *
 * The host decides whether there is a panel at all; the panel itself only knows
 * how to show a feed it is handed.
 */

/** Distance between the top bar and the panel, as in the design canvas. */
const PANEL_GUTTER = 12;

interface CompanionPanelHostProps {
  userId: string | null | undefined;
  brandId: string | null | undefined;
  /** Top bar plus any banner stacked above it. */
  topOffset: number;
  /** Full-bleed routes (the editors) hide the whole shell, this included. */
  hidden?: boolean;
}

export const CompanionPanelHost = ({
  userId,
  brandId,
  topOffset,
  hidden = false,
}: CompanionPanelHostProps) => {
  const { data: feed } = useCompanionFeedQuery(brandId ?? '');
  const scope = companionPanelScope(userId ?? '', brandId ?? '');
  const open = useCompanionPanelStore((state) => state.byScope[scope]?.open ?? false);
  const closePanel = useCompanionPanelStore((state) => state.closePanel);

  // A panel restored from a previous visit must not take the keyboard on page
  // load. Only an opening that happens while the app is running does.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const onClose = useCallback(() => closePanel(scope), [closePanel, scope]);

  if (hidden || !userId || !brandId || !feed?.availability.enabled || !open) return null;

  return (
    <CompanionPanel
      scope={scope}
      brandId={brandId}
      feed={feed}
      onClose={onClose}
      top={topOffset + PANEL_GUTTER}
      autoFocus={mountedRef.current}
    />
  );
};
